<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { isBefore } from 'date-fns';
import { useRoute } from 'vue-router';
import { formatDistanceToNow } from '@/utils/date-fns';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import { get } from 'lodash';
import CoverView from '@/views/shared/CoverView.vue';
import TTS from '@/components/shared/Tts.vue';
import i18n from '@/utils/i18n';
import DateTimeBlock from '@/components/shared/blocks/DateTimeBlock.vue';
import ParticipantsBlock from '@/components/shared/blocks/ParticipantsBlock.vue';
import DepartmentsBlock from '@/components/shared/blocks/DepartmentsBlock.vue';
import DescriptionBlock from '@/components/shared/blocks/DescriptionBlock.vue';
import PictogramsBlock from '@/components/shared/blocks/PictogramsBlock.vue';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import DeadlineBlock from '@/components/shared/blocks/DeadlineBlock.vue';
import PriceBlock from '@/components/shared/blocks/PriceBlock.vue';
import ClubBlock from '@/components/shared/blocks/ClubBlock.vue';
import InstitutionBlock from '@/components/shared/blocks/InstitutionBlock.vue';
import { useActivitiesStore } from '@/stores/activities';
import { useCacheStore } from '@/stores/cache';
import { useInstitutionStore } from '@/stores/institution';
import constants from '@/utils/constants';
import MediaBlock from '@/components/shared/blocks/MediaBlock.vue';
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';
import {
  ActivitySubscription,
  Profile_Type,
  ScreenProfile,
} from '@/graphql/generated-types';
import { eventBus } from '@/utils/eventBus';
import {
  ActivityFragment,
  ProfileFragment,
  SelectedActivityFragment,
} from '@/graphql/custom-types';

const cache = useCacheStore();
const institutionStore = useInstitutionStore();
const activitiesStore = useActivitiesStore();
const route = useRoute();

const participating = ref<boolean>(true);
const showAuthFlow = ref<boolean>(false);
const authFlowLoading = ref<boolean>(false);

const moduleColor = computed(() => {
  return route?.meta?.color;
});

const activityHideSignups = computed(() => {
  return get(institutionStore.settings, 'screen.activityHideSignups', false);
});

const imageUrl = computed(() => {
  return get(
    activitiesStore.selected,
    'coverImage.source',
    constants.placeholderImage
  );
});

const signupPossible = computed(() => {
  return (
    !maxSignupsReached.value &&
    activitiesStore.selected.signupPossible &&
    !activitiesStore.selected.cancelled
  );
});

const optoutPossible = computed(() => {
  return (
    !activitiesStore.selected.cancelled &&
    activitiesStore.selected.signupEnabled
  );
});

const signupExpired = computed(() => {
  if (!activitiesStore.selected.signupEnd) {
    return false;
  }

  return (
    !activitiesStore.selected.signupPossible &&
    !isBefore(new Date(), new Date(activitiesStore.selected.signupEnd))
  );
});

const maxSignupsReached = computed(() => {
  if (!activitiesStore.selected.maxSignups) {
    return false;
  }

  if (!activitiesStore.selected.participants) {
    return false;
  }

  return (
    activitiesStore.selected.participants.length >=
    activitiesStore.selected.maxSignups
  );
});

onMounted(() => {
  cache.use(
    'activitiesStore.getSelectedActivity',
    activitiesStore.getSelectedActivity,
    { activityId: route.params.id }
  );
});

function formatDeadline(activity: SelectedActivityFragment) {
  if (activity.signupPossible && activity.signupEnd) {
    return `${i18n.global.t('global.signUpDeadline')} ${formatDistanceToNow(new Date(activity.signupEnd), { addSuffix: true })}`;
  }
  return i18n.global.t('global.signupNotNeeded');
}

const setParticipating = (isParticipating: boolean) => {
  participating.value = isParticipating;
  showAuthFlow.value = true;
};

const save = async (profile: ProfileFragment, pin: string) => {
  try {
    authFlowLoading.value = true;
    const subscription: ActivitySubscription = {
      id: activitiesStore.selected.id,
    };

    const screenProfile: ScreenProfile = {
      profileId: profile.id,
      profileType: profile.type as Profile_Type,
      pin,
    };

    await activitiesStore.activitySignupScreen(
      participating.value,
      subscription,
      screenProfile
    );
    eventBus.emit('success', successOverlayTitle.value);
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showAuthFlow.value = false;
  }
};

const pinOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('activities.signUp')
    : i18n.global.t('activities.optOut');
});

const pinOverlayTitleBold = computed(() => {
  return activitiesStore.selected.title;
});

const successOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('activities.signedUp')
    : i18n.global.t('activities.optedOut');
});

const price = computed(() => {
  if (!activitiesStore.selected.price || !activitiesStore.selected.price[0]) {
    return undefined;
  }

  const [first] = activitiesStore.selected.price;
  if (!first.price) {
    return undefined;
  }

  return first;
});
</script>

<template>
  <div class="activity" v-if="activitiesStore.selected">
    <CoverView
      v-if="activitiesStore.selected.id"
      :image-url="imageUrl"
      :module-color="moduleColor"
    >
      <div class="activityContent tts-content">
        <div class="flex items-start justify-between">
          <TitleBlock class="grow" :text="activitiesStore.selected.title" />
          <TTS />
        </div>

        <MediaBlock
          :pictures="activitiesStore.selected.pictures || []"
          :videos="activitiesStore.selected.videos || []"
          :documents="activitiesStore.selected.documents || []"
          :color="moduleColor as any"
        />

        <BlocksWrapper>
          <InstitutionBlock
            :title="activitiesStore.selected.institution?.name"
            :image="activitiesStore.selected.institution?.logo?.source"
            v-if="
              institutionStore.isExternalInstitution(
                activitiesStore.selected?.institution?.id || ''
              )
            "
          />
          <PictogramsBlock
            v-if="activitiesStore.selected.pictograms?.length"
            :pictograms="activitiesStore.selected.pictograms"
          />
          <DescriptionBlock
            :description="activitiesStore.selected.description || ''"
          />

          <DateTimeBlock
            :start="activitiesStore.selected.startDate"
            :end="activitiesStore.selected.endDate ?? ''"
          />

          <DeadlineBlock
            v-if="activitiesStore.selected.signupPossible"
            :signup-expired="signupExpired"
            :text="formatDeadline(activitiesStore.selected)"
          />

          <PriceBlock
            v-if="!!price && price.price"
            :text="$n(price.price / 100, 'currency')"
          />

          <ParticipantsBlock
            v-if="
              activitiesStore.selected.participants?.length &&
              !activityHideSignups
            "
            can-trigger-overlay
            participant-type="participant"
            :profiles="activitiesStore.selected.participants"
          />

          <ParticipantsBlock
            v-if="activitiesStore.selected.responsible?.length"
            can-trigger-overlay
            participant-type="responsible"
            :profiles="activitiesStore.selected.responsible"
          />

          <DepartmentsBlock
            v-if="activitiesStore.selected.departments?.length"
            :departments="activitiesStore.selected.departments"
          />

          <BasicBlock
            v-if="maxSignupsReached"
            :icon="['fal', 'user-slash']"
            :text="$t('global.maxSignupsReached')"
            :ariaDescription="$t('global.maxSignupsReached')"
            class="text-red-400"
          />

          <ClubBlock
            v-if="activitiesStore.selected.club"
            :image="activitiesStore.selected.club.logo.source"
            :text="activitiesStore.selected.club.name"
          />

          <BasicBlock
            v-if="
              !institutionStore.isExternalInstitution(
                activitiesStore.selected?.institution?.id || ''
              )
            "
            :icon="['fal', 'fa-hotel']"
            :text="activitiesStore.selected.institution?.name"
            :borderBottom="false"
          />
        </BlocksWrapper>
      </div>

      <div class="activityActions w-max left-full sticky">
        <ButtonEl
          v-if="signupPossible"
          text="global.signUp"
          :icon="['far', 'circle-plus']"
          background="bg-green-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-51.25 mb-4"
          rounded="full"
          @click="setParticipating(true)"
        />

        <ButtonEl
          v-if="optoutPossible"
          text="global.optOut"
          :icon="['far', 'minus-circle']"
          background="bg-red-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-51.25"
          rounded="full"
          @click="setParticipating(false)"
        />
      </div>
    </CoverView>

    <AuthFlow
      v-if="showAuthFlow"
      :participating="participating"
      :shared-departments="activitiesStore.selected.departments || []"
      :participants="activitiesStore.selected.participants || []"
      :title="pinOverlayTitle"
      :title-bold="pinOverlayTitleBold"
      :loading="authFlowLoading"
      @close="showAuthFlow = false"
      @authenticated="(profile, pin) => save(profile, pin)"
    />
  </div>
</template>

<style lang="scss">
.activity {
  .activityActions {
    bottom: 70px;
    padding-right: 32px;
  }
}
</style>
